// Shared mutable client state.
//
// `keys` is mutated in place (never reassigned), so it's `const`. Values that
// get *reassigned* (the local player, flags, camera angle, time of day) live as
// fields on `game` so other modules can update them — you can't reassign an
// imported binding.

import type * as THREE from "three";
import { freshVert } from "./sim/vertical.ts";

// Currently-held keys (lowercased).
export const keys = new Set<string>();

export const game = {
  // The local player. Rebuilt with the chosen shirt color once the user signs in.
  player: null as unknown as THREE.Group,
  joined: false, // has the user signed in (vs. the sign-in screen)?
  camYaw: 0, // camera orbit angle around the player (Q/E)
  tod: 0.3, // time of day [0,1); starts mid-morning
  feetY: 0, // the hero's FEET height, mirrored from vert.y each frame (rises with a jump/fall arc now — no separate jump offset)
  // The real vertical state (gravity solver, sim/vertical.ts): height, vertical
  // velocity, airborne flag, coyote/buffer windows. game.ts steps it each frame;
  // corrections/teleports reset it via groundVert.
  vert: freshVert(0),
  hp: 20, // current hitpoints
  maxHp: 20,
  down: false, // knocked out (HP hit 0) — movement/attacks disabled until revived
  talking: false, // in a conversation (talk.ts) — movement/jump/attacks locked; cutscene camera
  talkExit: 0,    // seconds left easing the camera back from the cutscene to the follow pose
  journalOpen: false, // reading the journal (journal.ts) — movement/jump/attacks locked; reading camera
  journalExit: 0,     // seconds left easing the camera back from the reading pose to the follow pose
  fishing: false, // a line is out (fishing.ts, plan 10) — movement/jump/attacks locked; first-person angling camera
  fishingExit: 0, // seconds left easing the camera back from the angling shot to the follow pose
  catchOpen: false, // the catch-preview HUD is up (fishing.ts) — pick what to keep; movement/jump/attacks locked
  aiming: false,  // the hunting bow is drawn (hunting.ts, plan 11) — movement/jump locked; over-the-shoulder aim camera
  aimingExit: 0,  // seconds left easing the camera back from the aim shot to the follow pose
  cooking: false, // a cook is on the fire (cooking.ts, plan 18) — movement/jump/attacks locked; first-person over-the-fire camera
  cookingExit: 0, // seconds left easing the camera back from the cook shot to the follow pose
  chopping: false, // mid tree-cut minigame (woodcutting.ts) — movement/jump/attacks locked
  placingFire: false, // firestarting placement pick (4 adjacent orange tiles) — movement locked until choose/cancel
  fireStarting: false, // mid firestart minigame (firestarting.ts) — movement/jump/attacks locked
  transferring: false, // mid map-swap (game.ts swapWorld, plan 24) — input + outbound moves locked for the fade/teardown/rebuild window
  pose: null as string | null, // held body pose (seated/prone/… — /sit emotes + click-to-sit). SERVER-CONFIRMED via our own snapshot record (the talker pattern); moving/attacking breaks it server-side
  cinematic: false, // a cinematic is playing (src/cinematic, plan 26) — world input suspended; the cinematic runtime owns the camera + the whole frame (no exit ease: every entry/exit hides behind a fade)
  // Live combat target (focused fightable or auto-engaged). combat.ts sets this
  // each frame; doors leave F alone while true so the primary attack owns F.
  fighting: false,
  /** Long-shot charge in progress (combat.ts). Roots walk/jump; free cancel on WASD/jump. */
  bowCharging: false,
  /** Charge fully ready — subtle FOV pull (camera.ts). */
  bowChargeReady: false,
  /** 0..1 charge progress for HUD dials (ability slot + focus card). */
  bowChargeFrac: 0,

  stamina: 5, // current stamina
  maxStamina: 5,
  running: false,   // sprinting this frame (Shift + has stamina) — drains stamina
  roadBoost: false, // on a road >1s — 1.25× move speed + stamina regen (set in game.ts)
  // Crowd-control (server-owned via vitals + combatFx; client mirrors for local
  // input locks so prediction doesn't fight corrections).
  snareT: 0, // seconds remaining rooted (can still attack)
  stunT: 0,  // seconds remaining frozen (no move, no act)
  slowT: 0,  // seconds remaining slowed (move at slowMult)
  slowMult: 1, // speed mult while slowT > 0 (e.g. 0.7)
  kbLocal: false, // mid server-driven knockback residual (follow corrections)
  kbT: 0, // seconds left treating transform as server-owned (combatFx refresh)
};

/** THE input-lock predicate: some modal or activity that owns input + camera is
 *  active (knocked out, talking, reading the journal, fishing, picking a catch,
 *  drawing a bow, or cooking), so world actions — move/jump/attack/interact/cast/
 *  draw/cook/open-book — must not fire. ONE definition so adding a locking
 *  activity is a single edit here, not a sweep of copy-pasted OR-chains across
 *  every gate (they had already drifted: some omitted `aiming`, some `cooking`).
 *  Each activity module still checks its OWN flag separately where it must tell
 *  "start" from "already active"; this is the "is anything else holding input" test. */
export function inputLocked(): boolean {
  return game.down || game.talking || game.journalOpen || game.fishing || game.catchOpen || game.aiming || game.cooking || game.chopping || game.placingFire || game.fireStarting || game.transferring || game.cinematic;
}
